import React, { useEffect, useState, useRef } from 'react';
import { toJsDate } from '../utils/dateUtils';
import { fetchRecentlyAddedMedia, compactRecentlyAddedItems } from '../utils/mediaUtils';
import MediaCard from '../components/MediaCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';

const ITEMS_PER_PAGE = 24;
const CARD_WIDTH = 160;
const GRID_GAP = 20;

function RecentlyAdded({ onItemClick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const [columns, setColumns] = useState(1);
  const [contentWidth, setContentWidth] = useState(CARD_WIDTH);
  const cardsGridRef = useRef(null);
  const firstCardRef = useRef(null);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const recent = await fetchRecentlyAddedMedia({ movieLimit: 100, episodeLimit: 160 });
        setItems(compactRecentlyAddedItems(recent));
        setPage(1);
      } catch (err) {
        setError('Failed to load recently added items.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  useEffect(() => {
    const recalcLayout = () => {
      const isDesktopPadding = window.innerWidth >= 600;
      const sidePadding = isDesktopPadding ? 24 : 16;
      const availableWidth = Math.max(0, window.innerWidth - sidePadding * 2);
      const nextColumns = Math.max(1, Math.floor((availableWidth + GRID_GAP) / (CARD_WIDTH + GRID_GAP)));
      const nextContentWidth = nextColumns * CARD_WIDTH + (nextColumns - 1) * GRID_GAP;

      const gridTop = cardsGridRef.current?.getBoundingClientRect().top ?? 260;
      const measuredCardHeight = firstCardRef.current?.getBoundingClientRect().height ?? 300;
      const availableHeight = Math.max(0, window.innerHeight - gridTop - 16);
      const nextRows = Math.max(1, Math.floor((availableHeight + GRID_GAP) / (measuredCardHeight + GRID_GAP)));
      const nextPageSize = Math.max(1, nextColumns * nextRows);

      setColumns(nextColumns);
      setContentWidth(nextContentWidth);
      setPageSize(nextPageSize);
    };

    const rafId = window.requestAnimationFrame(recalcLayout);
    window.addEventListener('resize', recalcLayout);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', recalcLayout);
    };
  }, [items.length, page]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const startIndex = (page - 1) * pageSize;
  const visibleItems = items.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  if (loading) return <LoadingSpinner message="Loading recently added…" />;

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 4 }}>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {items.length === 0 && !error ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">Nothing confirmed in the catalogue yet.</Typography>
        </Box>
      ) : (
        <Box sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ width: '100%', maxWidth: `${contentWidth}px`, mx: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {Math.min(startIndex + 1, items.length)}-
                {Math.min(startIndex + pageSize, items.length)} of {items.length}
              </Typography>
              {totalPages > 1 && (
                <Pagination
                  page={page}
                  count={totalPages}
                  color="primary"
                  size="small"
                  onChange={(_, nextPage) => setPage(nextPage)}
                />
              )}
            </Box>

            <Box
              ref={cardsGridRef}
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, ${CARD_WIDTH}px)`,
                gap: 2.5,
                justifyContent: 'center',
              }}
            >
              {visibleItems.map((item, index) => (
                <Box key={item.id} ref={index === 0 ? firstCardRef : null}>
                  <MediaCard
                    item={item}
                    subtitle={item.type === 'tv' ? (item.episodeLabel || '') : undefined}
                    onClick={() => onItemClick && onItemClick(item.showItem || item)}
                  />
                  {toJsDate(item.releaseDate) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
                    >
                      {toJsDate(item.releaseDate).toLocaleDateString('en-AU', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default RecentlyAdded;
